import { Product, SearchProductsRequest } from '../../clients/quipli-service/product/product';
import { useProductApiV1 } from '../../clients/quipli-service/product/product-api-v1';
import { useEffect, useRef, useState } from 'react';
import { ScrollableList } from '../scrollable-list/scrollable-list';
import { FieldLabel } from '../field/field';
import Icon from '../icons/icon';
import './product-auto-complete.scss';
import { Button } from '../form/button';
import { usePopper } from 'react-popper';
import { useOnClickOutside } from '../../util/use-on-click-outside';
import usePrevious from '../../hooks/use-previous';

export const ProductAutoComplete = (
  params: {
    value?: Product;
    onSelect: (product?: Product) => void;
  } & Omit<SearchProductsRequest, 'keyword' | 'pageNumber' | 'pageSize'>,
) => {
  const { onSelect, value, ...request } = params;
  const [keyword, setKeyword] = useState<string>(value?.name || '');
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const { searchProducts } = useProductApiV1(); // Popper elements for dropdown
  const [controlRef, setControlRef] = useState<HTMLDivElement | null>(null);
  const [dropdownRef, setDropdownRef] = useState<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [wrapperWidth, setWrapperWidth] = useState<number>(0);
  const { styles, attributes } = usePopper(controlRef, dropdownRef, {
    placement: 'bottom',
    strategy: 'fixed',
  });
  const prevValue = usePrevious(value);

  useEffect(() => {
    if (!value && prevValue && prevValue.name === keyword) {
      setKeyword('');
    }
  }, [value, prevValue, keyword]);

  // Resize observer to get wrapper width
  useEffect(() => {
    if (!controlRef) return;

    const resizeObserver = new ResizeObserver(() => {
      setWrapperWidth(controlRef?.scrollWidth ?? 0);
    });

    resizeObserver.observe(controlRef);

    return () => {
      resizeObserver.disconnect();
    };
  }, [controlRef]);

  useEffect(() => {
    if (value) {
      setKeyword(value.name);
    }
  }, [value]);

  // Close dropdown when clicked outside
  useOnClickOutside(wrapperRef, () => setIsExpanded(false));

  return (
    <div className="product-auto-complete flex flex-column relative" ref={wrapperRef}>
      <FieldLabel label={'Product'} isRequired={true} />
      <div className="product-auto-complete-input-group" ref={setControlRef}>
        <Icon icon="Search" />
        <input
          className="product-search flex-auto border-none"
          type="text"
          placeholder={'Search Products'}
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value);
            onSelect(undefined);
          }}
          data-cy="product_autocomplete_keyword"
          onFocus={() => setIsExpanded(true)}
        />
        <Button className="button-tertiary">
          <Icon size="small" icon={isExpanded ? 'ChevronUp' : 'ChevronDown'} />
        </Button>
      </div>

      {isExpanded && (
        <div
          className={`result-container bg-quipli-white border border-solid border-grey-100 rounded-[9px] shadow-md mt-2 p-1 overflow-y-auto`}
          ref={setDropdownRef}
          style={{ ...styles.popper, zIndex: 5, width: `${wrapperWidth}px`, maxHeight: '238px' }}
          {...attributes.popper}
        >
          <ScrollableList<Product>
            fetch={(pagination) => searchProducts({ ...request, keyword, ...pagination })}
            onSelect={(p) => {
              onSelect(p);
              setIsExpanded(false);
            }}
            value={value}
            itemLabel={(p) => p.name}
            itemKey={(p) => p.id}
          />
        </div>
      )}
    </div>
  );
};
